<template>
  <BarChart
    :data="calculatedData"
    :options="chartOptions"
    :plugins="plugins"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Bar as BarChart } from "vue-chartjs"
import type {
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js"
import "chartjs-adapter-date-fns"

export type HoveredDataPoint = {
  index: number | undefined
  datasets: {
    label: string
    value: number
    color?: string
  }[]
  formattedLabel: string
}

const { formatToCurrencyString } = useCurrency()

type DateRange = {
  start: Date
  end: Date
}

ChartJS.register(...registerables)
const props = withDefaults(
  defineProps<{
    id: string
    datasets: {
      label: string
      data: number[]
      color?: string | undefined
    }[]
    labels: DateRange[]
    granularity?: Granularity | undefined
    currency: string
  }>(),
  {
    granularity: undefined,
  }
)
const emit = defineEmits<{
  (
    event: "hover-datapoint",
    datapoint: HoveredDataPoint
  ): void
}>()

const calculatedLabels = computed(() => {
  return props.labels.map(({ start }) => start)
})

const calculatedDatasets = computed(() =>
  props.datasets.map((dataset) => ({
    label: dataset.label,
    data: dataset.data,
    pointRadius: 0,
    backgroundColor: dataset.color,
  }))
)

const calculatedData = computed<
  ChartData<"bar", number[], Date>
>(() => ({
  labels: calculatedLabels.value,
  datasets: calculatedDatasets.value,
}))
const colorText = useCssVar("--color-on-background")

const chartOptions = computed<ChartOptions<"bar">>(() => ({
  maintainAspectRatio: false,
  animation: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
      position: "right",
      labels: {
        pointStyle: "rectRounded",
        usePointStyle: true,
      },
    },
    tooltip: {
      enabled: false,
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
  },
  datasets: {
    bar: {
      borderRadius: 4,
    },
  },
  events: [
    "mousemove",
    "mouseout",
    "click",
    "touchstart",
    "touchmove",
    "touchend",
  ],
  scales: {
    x: {
      stacked: false,
      type: "timeseries",
      time: {
        displayFormats: {
          month: "MMM",
        },
        unit: props.granularity,
        isoWeekday: true,
      },
      ticks: {
        font: {
          size: 12,
        },
        textStrokeColor: `rgb(${colorText.value} / 0.7)`,
        minRotation: 0,
        maxRotation: 0,
        padding: 8,
        align: "center",
        autoSkipPadding: 8,
        includeBounds: false,
      },
      grid: {
        color: `rgb(${colorText.value} / 0.05)`,
        drawTicks: false,
      },
    },
    y: {
      suggestedMax: 10000,
      title: {
        display: false,
      },
      grid: {
        display: false,
        drawTicks: false,
      },
      ticks: {
        align: "center",
        font: {
          size: 12,
        },
        callback: (value) =>
          value !== 0
            ? formatToCurrencyString(
                Number(value),
                props.currency,
                {
                  compact: true,
                  stripTrailingZeros: true,
                }
              )
            : "",
      },
      stacked: false,
      beginAtZero: true,
    },
  },
}))

const cumulativeTotals = computed(() =>
  props.datasets.map((dataset) =>
    dataset.data.reduce(
      (accumulator, value) => accumulator + value,
      0
    )
  )
)
const minStart = computed(() =>
  DateFns.min(props.labels.map(({ start }) => start))
)
const maxEnd = computed(() =>
  DateFns.max(props.labels.map(({ end }) => end))
)
const hoveredDatapointForWholeRange =
  computed<HoveredDataPoint>(() => ({
    index: undefined,
    datasets: props.datasets.map((dataset, index) => ({
      label: "",
      value: cumulativeTotals.value[index],
      color: dataset.color,
    })),
    formattedLabel: buildFormattedLabel({
      start: minStart.value,
      end: maxEnd.value,
    }),
  }))
const plugins: Plugin<"bar">[] = [
  {
    id: "hoverEmitIndex",
    afterDatasetDraw: (chart_) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()

      const activePoint =
        activeElements && activeElements.length > 0
          ? activeElements[0]
          : undefined
      const index_ = activePoint
        ? activePoint.index
        : undefined

      const hoveredDatapoint =
        index_ !== undefined
          ? {
              index: index_,
              datasets: props.datasets.map((dataset) => ({
                label: "",
                value: dataset.data[index_],
                color: dataset.color,
              })),
              formattedLabel: buildFormattedLabel(
                props.labels[index_],
                props.granularity
              ),
            }
          : hoveredDatapointForWholeRange.value
      emit("hover-datapoint", hoveredDatapoint)
    },
    afterEvent: (chart, { event }) => {
      if (event.native?.type === "touchend") {
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
        chart.setActiveElements([])
        chart.update()
      }
    },
  },
]

const buildFormattedLabel = (
  { start, end }: { start: Date; end: Date },
  granularity?:
    | "hour"
    | "day"
    | "week"
    | "month"
    | "range"
    | "overview"
    | undefined
) => {
  return granularity === "hour"
    ? `${formatToTime(start)} - ${formatToTime(end)}`
    : granularity === "day"
      ? formatToDate(start)
      : `${formatToDate(start)} - ${formatToDate(end)}`
}
</script>
